/**
 * V·IAnet Governance — Admin shell tokens
 *
 * Fuente única de estilos para los primitivos `AdminPageHeader`, `AdminTableShell`,
 * `AdminDetailPanel`, `AdminPagination`, `AdminActionButton` y `AdminEmptyState`.
 *
 * Diseñados para casar pixel-perfect con el canónico visual del prototipo
 * (header azul corporativo `#0E243E`, hover `#F0F4FF`, botón primario 8x18 radius 8).
 *
 * Mantener consistente con `@/lib/design`. Si un valor aparece en ambos lugares
 * `@/lib/design` prevalece (este fichero lo importa).
 */

import { C } from '@/lib/design';

export const ADMIN_TOKENS = {
  header: {
    bg: C.azulMuyOscuro, // '#0E243E'
    color: '#FFFFFF',
    padding: '10px 12px',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.2,
  },
  row: {
    borderBottom: '1px solid #F0F0F0',
    hover: '#F0F4FF',
    padding: '9px 12px',
    fontSize: 13,
    color: C.text,
  },
  table: {
    border: '1px solid #E5E7EB',
    boxShadow: '0 1px 4px rgba(15,23,42,0.07)',
    borderRadius: 8,
    background: '#FFFFFF',
  },
  button: {
    primary: {
      background: C.azul2, // '#2F5496'
      color: '#FFFFFF',
      padding: '8px 18px',
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 600,
      fontFamily: 'DM Sans, sans-serif',
      border: 'none',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
    },
    secondary: {
      background: '#FFFFFF',
      color: C.azul2,
      padding: '8px 14px',
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 600,
      fontFamily: 'DM Sans, sans-serif',
      border: `1px solid ${C.azul2}`,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
    },
  },
  code: {
    fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, monospace',
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: 0.2,
  },
  search: {
    width: 220,
    height: 32,
    padding: '6px 12px',
    borderRadius: 6,
    border: '1px solid #E5E7EB',
    fontSize: 13,
    fontFamily: 'DM Sans, sans-serif',
    outline: 'none',
  },
  h2: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: C.azulMuyOscuro,
    fontFamily: 'DM Sans, sans-serif',
    display: 'inline-flex',
    alignItems: 'center',
  },
  headerRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap' as const,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap' as const,
  },
  pagination: {
    bar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 14px',
      borderTop: '1px solid #F0F0F0',
      background: '#FAFAFB',
      fontSize: 12,
      color: C.textSec,
      fontFamily: 'DM Sans, sans-serif',
    },
    button: {
      padding: '4px 10px',
      border: '1px solid #E5E7EB',
      borderRadius: 6,
      background: '#FFFFFF',
      cursor: 'pointer',
      fontSize: 12,
      fontFamily: 'DM Sans, sans-serif',
      color: C.text,
    },
    buttonDisabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
  },
  actionButton: {
    root: {
      width: 28,
      height: 28,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 6,
      border: '1px solid #E5E7EB',
      background: '#FFFFFF',
      cursor: 'pointer',
      color: C.gray700,
      transition: 'all 120ms ease',
    },
    hover: {
      background: '#F0F4FF',
      borderColor: C.azul2,
      color: C.azul2,
    },
    danger: {
      color: C.error,
    },
  },
  empty: {
    root: {
      padding: '48px 16px',
      textAlign: 'center' as const,
      color: C.textSec,
      fontSize: 13,
      fontFamily: 'DM Sans, sans-serif',
    },
  },
  panel: {
    overlay: {
      position: 'fixed' as const,
      inset: 0,
      background: 'rgba(15,23,42,0.32)',
      zIndex: 900,
      animation: 'via-overlay-fade-in 140ms ease-out',
    },
    root: {
      position: 'fixed' as const,
      top: 0,
      right: 0,
      bottom: 0,
      width: '70%',
      maxWidth: 960,
      background: '#FFFFFF',
      boxShadow: '-10px 0 32px rgba(15,23,42,0.18)',
      zIndex: 901,
      display: 'flex',
      flexDirection: 'column' as const,
      animation: 'via-panel-slide-in 180ms ease-out',
    },
    header: {
      padding: '16px 20px',
      borderBottom: '1px solid #E5E7EB',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: '#F8FAFC',
    },
    body: {
      flex: 1,
      overflowY: 'auto' as const,
      padding: 20,
    },
    footer: {
      padding: '12px 20px',
      borderTop: '1px solid #E5E7EB',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 8,
      background: '#F8FAFC',
    },
  },
} as const;

export type AdminTokens = typeof ADMIN_TOKENS;
